import axios from "axios";

/**
 * Shared logic for the AI Tinkerpreneur project-submission flow.
 *
 * This layer is intentionally presentation-free and year-agnostic so it can be reused
 * unchanged by every year's (bespoke) submission page. It talks to the backend at
 * /v2/tinkerpreneur/submission/*.
 */

const SUBMISSION_BASE = `${process.env.REACT_APP_API_URL}/v2/tinkerpreneur/submission`;

const authHeaders = () => ({
  "x-access-token": localStorage.getItem("jwtToken"),
});

/**
 * Eligibility + submission-window state for the logged-in participant.
 * @returns {Promise<{canSubmit:boolean, eligible:boolean, currentCount:number, maxAllowed:number, submissionOpen:boolean, deadline:string|null}>}
 */
export const fetchCanSubmit = async () => {
  const res = await axios.get(`${SUBMISSION_BASE}/can-submit`, {
    headers: authHeaders(),
  });
  return res.data;
};

/**
 * The participant's existing submission(s), used to prefill the form and to drive the
 * read-only state once an entry is confirmed.
 */
export const fetchEntry = async () => {
  const res = await axios.get(`${SUBMISSION_BASE}/entry`, {
    headers: authHeaders(),
  });
  return res.data.data;
};

/**
 * Submit the confirmed project(s).
 * @param {FormData} formData - built via buildSubmissionFormData
 */
export const confirmSubmission = async (formData) => {
  const res = await axios.post(`${SUBMISSION_BASE}/confirm`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      ...authHeaders(),
    },
  });
  return res.data.data;
};

/**
 * Build the multipart payload from Formik values for a single project submission.
 *
 * @param {object} values - Formik values
 * @param {string} [sourceKey="iaiif_source"] - localStorage key holding a referral source
 * @returns {FormData}
 */
export const buildSubmissionFormData = (values, sourceKey = "iaiif_source") => {
  const formData = new FormData();

  formData.append("readGuideline", values.readGuideline);

  const project = values.projects[0];
  if (project && !project.isExisting) {
    formData.append("project[0][theme]", project.theme);
    formData.append("project[0][ethicalConcern]", project.ethicalConcern);
    formData.append("project[0][sdg]", project.sdg);
    formData.append("project[0][projectType]", project.projectType);
    formData.append("project[0][videoURL]", project.videoURL);
    formData.append(
      "project[0][hasDeployedInnovation]",
      project.hasDeployedInnovation
    );
    // Only sent on "yes" so toggling back to "no" cannot leave a stale link behind.
    if (project.hasDeployedInnovation === "yes") {
      formData.append(
        "project[0][journeyDocumentURL]",
        project.journeyDocumentURL
      );
    }

    (project.members || []).forEach((member) => {
      formData.append("project[0][members][]", member);
    });

    if (project.video) {
      formData.append("project[0][video]", project.video);
    }
    if (project.pdf) {
      formData.append("project[0][pdf]", project.pdf);
    }
  }

  const source = localStorage.getItem(sourceKey);
  if (source) {
    formData.append("source", source);
  }

  return formData;
};
